import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { debounce } from "lodash-es";
import { Component, type ReactElement } from "react";
import { type InjectedFormProps, reduxForm, SubmissionError } from "redux-form";
import { Callout, CalloutIntent } from "widgets";

import { simpleFieldValidation } from "../../app-utils/form.js";
import { type InputUserGroupInput, type UserGroup } from "../../graphql.js";
import { validateUsergroupNameIsNotUsed } from "../../redux/modules/user-group-admin.js";
import { Dialog } from "../core/overlays/Dialogs.js";
import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";

export interface IEditUserGroupDialogProps extends InjectedFormProps<any, any> {
  userGroup: UserGroup;
  onEditUserGroup: (payload: InputUserGroupInput) => Promise<UserGroup>;

  onApply?: () => void;
  onCancel?: () => void;
}

class EditUserGroupDialogImpl extends Component<IEditUserGroupDialogProps> {
  public static EDIT_USER_GROUP_FORMNAME = "editUserGroup";
  private static ASYNC_VALIDATION_DEBOUNCE_TIMER = 300;

  public static USER_GROUP_DIALOG_FIELD_NAME = "userGroupName";
  public static USER_GROUP_DIALOG_FIELD_DESCRIPTION = "userGroupDescription";

  constructor(props: IEditUserGroupDialogProps) {
    super(props);
    this.handleSubmitData = this.handleSubmitData.bind(this);
    this.debouncedNameValidation = this.debouncedNameValidation.bind(this);
    this.initFormValues = this.initFormValues.bind(this);
  }

  initFormValues(props: IEditUserGroupDialogProps) {
    props.change(EditUserGroupDialogImpl.USER_GROUP_DIALOG_FIELD_NAME, props.userGroup.name);
    props.change(EditUserGroupDialogImpl.USER_GROUP_DIALOG_FIELD_DESCRIPTION, props.userGroup.description);
  }

  componentDidMount() {
    this.debouncedNameValidation = debounce(
      this.debouncedNameValidation,
      EditUserGroupDialogImpl.ASYNC_VALIDATION_DEBOUNCE_TIMER,
    );
    this.initFormValues(this.props);
  }

  debouncedNameValidation() {
    this.props.asyncValidate();
  }

  handleSubmitData(values): Promise<UserGroup> {
    const payload: InputUserGroupInput = {
      userGroupId: this.props.userGroup.id,
      name: values[EditUserGroupDialogImpl.USER_GROUP_DIALOG_FIELD_NAME].trim(),
      description: values[EditUserGroupDialogImpl.USER_GROUP_DIALOG_FIELD_DESCRIPTION] || null,
    };

    return this.props.onEditUserGroup(payload).then(
      (fulfilled) => {
        this.props.onApply();
        this.props.reset();
        return fulfilled;
      },
      // the following will throw an error that redux-form understands as a submission error, with a general error that will be
      // available from this.props.error
      (rejected) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  }

  render(): ReactElement {
    return (
      <Dialog
        headerProps={{ title: "Modify User Group" }}
        mainActionButtonProps={{
          children: "Modify User Group",
          isDisabled: this.props.submitting || this.props.invalid,
          onPress: this.props.handleSubmit(this.handleSubmitData),
        }}
        closeFn={() => {
          this.props.reset();
          this.props.onCancel();
        }}
      >
        <InputReduxFormField
          componentClass="input"
          name={EditUserGroupDialogImpl.USER_GROUP_DIALOG_FIELD_NAME}
          label="User group name"
          placeholder="Enter user group name"
          maxLength={DB_FIELD_MAX_LENGTH.USERGROUP.NAME}
          component={InputField}
          handleChange={this.debouncedNameValidation}
          validate={simpleFieldValidation}
        />

        <InputReduxFormField
          componentClass="textarea"
          name={EditUserGroupDialogImpl.USER_GROUP_DIALOG_FIELD_DESCRIPTION}
          label="Description"
          placeholder="Optional description"
          maxLength={DB_FIELD_MAX_LENGTH.USERGROUP.DESCRIPTION}
          component={InputField}
        />

        {this.props.error && <Callout intent={CalloutIntent.Danger}>{this.props.error}</Callout>}
      </Dialog>
    );
  }

  static async asyncValidate(values, dispatch, props) {
    if (!values) {
      return;
    }

    const userGroupName = values?.[EditUserGroupDialogImpl.USER_GROUP_DIALOG_FIELD_NAME].trim();

    if (userGroupName === props.userGroup.name) {
      return;
    }

    if (await dispatch(validateUsergroupNameIsNotUsed(userGroupName))) {
      throw {
        [EditUserGroupDialogImpl.USER_GROUP_DIALOG_FIELD_NAME]: "User group name is already in use",
      };
    }
  }
}

const EditUserGroupDialog = reduxForm<{}, any>({
  form: EditUserGroupDialogImpl.EDIT_USER_GROUP_FORMNAME,
  asyncValidate: EditUserGroupDialogImpl.asyncValidate,
  asyncBlurFields: [EditUserGroupDialogImpl.USER_GROUP_DIALOG_FIELD_NAME], // so that only blurring this field triggers the async validation
})(EditUserGroupDialogImpl);

export default EditUserGroupDialog;
